import { GENRE_NAMES, GENRES, type Genre } from '@mogzo/shared';
import { Play } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { NoticeCard } from '../components/community/Shell';
import { AppFrame } from '../components/frame/AppFrame';
import { GameCover, GameRow, GameTile, NewChip } from '../components/games/GameArt';
import { Alert, PageSpinner } from '../components/ui';
import { ApiError } from '../lib/api';
import { type DiscoverData, fetchDiscover } from '../lib/games';

type State =
  | { status: 'loading' }
  | { status: 'closed' }
  | { status: 'failed' }
  | { status: 'ready'; data: DiscoverData };

const chip = (current: boolean) =>
  `inline-flex h-9 shrink-0 items-center rounded-full border px-3.5 text-sm font-semibold ${current ? 'border-ink bg-ink text-white' : 'border-line bg-card hover:border-muted'}`;

/**
 * mogzo.com/discover (M29): every live game, newest first. All shows the newest game on top,
 * what's new and what's like the game you played last; `?genre=` lists one genre.
 */
export function Discover() {
  const [params] = useSearchParams();
  const asked = params.get('genre');
  const genre = GENRES.find((g) => g === asked) ?? null;
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    fetchDiscover()
      .then((data) => active && setState({ status: 'ready', data }))
      .catch((error) => {
        if (!active) return;
        setState({
          status: error instanceof ApiError && error.status === 404 ? 'closed' : 'failed',
        });
      });
    return () => {
      active = false;
    };
  }, []);

  if (state.status === 'loading') return <PageSpinner />;
  if (state.status === 'closed') {
    return (
      <AppFrame>
        <NoticeCard title="Page not found">
          <p>
            Check the address, or go back to{' '}
            <Link to="/" className="font-semibold text-ink underline">
              mogzo.com
            </Link>
            .
          </p>
        </NoticeCard>
      </AppFrame>
    );
  }

  const games = state.status === 'ready' ? state.data.games : [];
  const genres = GENRES.filter((g) => games.some((game) => game.genre === g));
  return (
    <AppFrame>
      <nav
        aria-label="Genres"
        className="flex gap-2 overflow-x-auto px-3 pt-3 [scrollbar-width:none] lg:mx-auto lg:max-w-[1000px] lg:px-6 lg:pt-6"
      >
        <Link to="/discover" aria-current={genre ? undefined : 'page'} className={chip(!genre)}>
          All
        </Link>
        {genres.map((g) => (
          <Link
            key={g}
            to={`/discover?genre=${g}`}
            aria-current={genre === g ? 'page' : undefined}
            className={chip(genre === g)}
          >
            {GENRE_NAMES[g]}
          </Link>
        ))}
      </nav>
      {state.status === 'failed' && (
        <div className="px-3 pt-4">
          <Alert>Couldn’t load Discover. Reload the page to try again.</Alert>
        </div>
      )}
      {state.status === 'ready' &&
        (genre ? <OneGenre genre={genre} data={state.data} /> : <All data={state.data} />)}
    </AppFrame>
  );
}

function All({ data }: { data: DiscoverData }) {
  const [featured] = data.games;
  const fresh = data.games.filter((game) => game.isNew && game.id !== featured?.id);
  if (!featured) {
    return (
      <main aria-label="Discover" className="px-3 pt-6 pb-10">
        <p className="rounded-[18px] border border-dashed border-line px-5 py-8 text-center text-muted">
          No games yet. They show here once they’re live.
        </p>
      </main>
    );
  }
  return (
    <main
      aria-label="Discover"
      className="mx-auto flex w-full max-w-[1000px] flex-col gap-6 px-3 pt-4 pb-10 lg:px-6"
    >
      <section aria-label="Newest game">
        <Link to={featured.path} className="group block">
          <GameCover game={featured} className="aspect-[16/9] rounded-[18px] sm:aspect-[16/6]">
            <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/70 to-transparent p-4 pt-12 text-white">
              <span className="min-w-0">
                {featured.isNew && <NewChip />}
                <span className="mt-1.5 block truncate font-display text-[26px] leading-tight font-bold">
                  {featured.name}
                </span>
                <span className="block truncate text-sm text-white/80">
                  {[featured.genre && GENRE_NAMES[featured.genre], `by ${featured.developer}`]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full bg-brand px-4 text-sm font-semibold text-ink">
                <Play size={14} fill="currentColor" aria-hidden="true" />
                Play
              </span>
            </span>
          </GameCover>
        </Link>
      </section>
      {fresh.length > 0 && <GameRow id="new-on-mogzo" title="New on MOGZO" games={fresh} />}
      {data.alike && (
        <GameRow
          id="because-you-played"
          title={`Because you played ${data.alike.name}`}
          games={data.alike.games}
        />
      )}
      <section aria-labelledby="every-game">
        <h2 id="every-game" className="px-1 text-lg leading-6 font-bold tracking-[-0.01em]">
          Every game
        </h2>
        <ul className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
          {data.games.map((game) => (
            <li key={game.id}>
              <GameTile game={game} />
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

function OneGenre({ genre, data }: { genre: Genre; data: DiscoverData }) {
  const games = data.games.filter((game) => game.genre === genre);
  const played = new Set(data.played);
  return (
    <main
      aria-labelledby="genre-title"
      className="mx-auto w-full max-w-[1000px] px-3 pt-5 pb-10 lg:px-6"
    >
      <div className="px-1">
        <h1 id="genre-title" className="text-2xl leading-8 font-bold tracking-[-0.015em]">
          {GENRE_NAMES[genre]}
        </h1>
        <span className="text-sm text-muted">
          {games.length} game{games.length === 1 ? '' : 's'} · newest first
        </span>
      </div>
      <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
        {games.map((game) => (
          <li key={game.id}>
            <GameTile
              game={game}
              line={
                <>
                  by {game.developer || 'its developer'}
                  {played.has(game.id) ? ' · in your library' : ''}
                  {game.isNew && (
                    <>
                      {' · '}
                      <NewChip />
                    </>
                  )}
                </>
              }
            />
          </li>
        ))}
      </ul>
    </main>
  );
}
